import type {
  ContextualBlock,
  InteractiveScriptOptions,
  InteractiveScriptResult,
  ScriptInfo,
  ScriptModeOption,
} from '../script-generator/types.js';
import {
  extractContextualBlocks,
  groupContextualBlocksByTarget,
} from '../script-generator/context-parser.js';
import { getScriptConverter } from './registry.js';

/**
 * Generate an interactive script from contextual blocks using the converter for the target script.
 */
export function generateInteractiveScript(
  blocks: ContextualBlock[],
  options: InteractiveScriptOptions = {}
): InteractiveScriptResult {
  if (blocks.length === 0) {
    return {
      success: false,
      error: 'No blocks provided',
    };
  }

  const converter = getScriptConverter(blocks[0].target);
  if (!converter.supportsOutputMode('interactive')) {
    return {
      success: false,
      error: `Interactive mode is not supported for ${blocks[0].target}.`,
    };
  }

  return converter.generateInteractiveScript(blocks, options);
}

/**
 * Generate all available script outputs from woven markdown + standard tangle output.
 */
export function generateAllScriptModes(
  wovenMarkdown: string,
  standardScripts: Record<string, string>,
  options: InteractiveScriptOptions = {}
): Map<string, ScriptInfo> {
  const result = new Map<string, ScriptInfo>();

  const contextualBlocks = extractContextualBlocks(wovenMarkdown);
  const groupedBlocks = groupContextualBlocksByTarget(contextualBlocks);

  for (const [scriptName, blocks] of groupedBlocks) {
    const converter = getScriptConverter(scriptName);
    const converterModes = converter.getOutputOptions() as ScriptModeOption[];
    const standard = standardScripts[scriptName] || '';
    const language = converter.detectScriptLanguage(blocks);

    let interactive: string | null = null;
    if (converter.supportsOutputMode('interactive') && converter.supportsInteractiveBlocks(blocks)) {
      const scriptOptions = {
        ...options,
        title: options.title || scriptName,
      };
      const interactiveResult = converter.generateInteractiveScript(blocks, scriptOptions);
      if (interactiveResult.success && interactiveResult.script) {
        interactive = interactiveResult.script;
      }
    }

    const outputs: Record<string, string> = { standard };
    if (interactive) {
      outputs.interactive = interactive;
    }

    result.set(scriptName, {
      name: scriptName,
      outputs,
      availableModes: converterModes,
      language,
      stepCount: blocks.length,
    });
  }

  for (const scriptName of Object.keys(standardScripts)) {
    if (!result.has(scriptName)) {
      const converter = getScriptConverter(scriptName);
      const converterModes = converter.getOutputOptions() as ScriptModeOption[];
      const standard = standardScripts[scriptName];

      result.set(scriptName, {
        name: scriptName,
        outputs: { standard },
        availableModes: converterModes,
        language: converter.detectScriptLanguage([]),
        stepCount: 0,
      });
    }
  }

  return result;
}

/**
 * Generate an interactive script directly from woven markdown for a target script.
 */
export function generateInteractiveScriptFromMarkdown(
  wovenMarkdown: string,
  targetScript: string,
  options: InteractiveScriptOptions = {}
): InteractiveScriptResult {
  const contextualBlocks = extractContextualBlocks(wovenMarkdown);
  const groupedBlocks = groupContextualBlocksByTarget(contextualBlocks);

  const blocks = groupedBlocks.get(targetScript);
  if (!blocks || blocks.length === 0) {
    return {
      success: false,
      error: `No blocks found for target script: ${targetScript}`,
    };
  }

  return generateInteractiveScript(blocks, {
    ...options,
    title: options.title || targetScript,
  });
}
